"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.stopEvent = exports.useAutocomplete = void 0;
var useAutocomplete_1 = __importDefault(require("@material-ui/lab/useAutocomplete"));
var lodash_1 = require("lodash");
var react_1 = require("react");
var SearchHooks_1 = require("../../../hooks/search/SearchHooks");
var arrayUtilities_1 = require("../../../util/arrayUtilities");
var Autocomplete_style_1 = __importDefault(require("./Autocomplete.style"));
function useAutocomplete(_a) {
    var controlled = _a.controlled, disableCloseOnSelect = _a.disableCloseOnSelect, disableFilter = _a.disableFilter, _b = _a.fontVariant, fontVariant = _b === void 0 ? 'body' : _b, getOptionDisabled = _a.getOptionDisabled, getOptionLabel = _a.getOptionLabel, getOptionSelected = _a.getOptionSelected, getOptionTooltipText = _a.getOptionTooltipText, groupBy = _a.groupBy, initialValue = _a.initialValue, multiple = _a.multiple, onChange = _a.onChange, onInputChange = _a.onInputChange, options = _a.options, value = _a.value, fuzzyPair = _a.fuzzyPair;
    var classes = (0, Autocomplete_style_1.default)({ fontVariant: fontVariant });
    var _c = __read((0, react_1.useState)(initialValue), 1), defaultValue = _c[0];
    var getOptionLabelWrapper = (0, react_1.useCallback)(function (option) { var _a; return ((_a = (getOptionLabel || lodash_1.toString)) === null || _a === void 0 ? void 0 : _a(option)) || ''; }, [getOptionLabel]);
    var filterOptions = (0, react_1.useCallback)(function (items, _a) {
        var inputValue = _a.inputValue, getOptionLabel = _a.getOptionLabel;
        var trimmedInput = inputValue.trim();
        if (disableFilter || !trimmedInput) {
            return options;
        }
        if (typeof fuzzyPair === 'undefined') {
            return (0, SearchHooks_1.getFuzzySearchResults)({
                values: items.map(function (option, index) { return ({ id: index.toString(), option: option }); }),
                searchText: trimmedInput,
                fuzzyPair: {
                    calculateAttributes: function (_a) {
                        var id = _a.id, option = _a.option;
                        return ({ id: id, label: getOptionLabel(option) });
                    },
                    keys: {
                        label: { weight: 1 },
                    },
                },
            }).map(function (_a) {
                var option = _a.option;
                return option;
            });
        }
        return (0, SearchHooks_1.getFuzzySearchResults)({
            values: items.map(function (option, index) { return (__assign({ id: index.toString() }, option)); }),
            searchText: trimmedInput,
            fuzzyPair: fuzzyPair,
        });
    }, [disableFilter, fuzzyPair, options]);
    var getOptionSelectedWrapper = (0, react_1.useCallback)(function (option, value) {
        if (getOptionSelected) {
            return getOptionSelected(option, value);
        }
        var optionLabel = getOptionLabelWrapper(option);
        var valueLabel = getOptionLabelWrapper(value);
        if (!optionLabel || !valueLabel) {
            return (0, lodash_1.isEqual)(option, value);
        }
        return optionLabel === valueLabel && (!groupBy || groupBy(option) === groupBy(value));
    }, [getOptionLabelWrapper, getOptionSelected, groupBy]);
    var allOptions = (0, react_1.useMemo)(function () { return (value ? (0, lodash_1.unionWith)((0, arrayUtilities_1.ensureArray)(value), options, getOptionSelectedWrapper) : options); }, [getOptionSelectedWrapper, options, value]);
    // filteredOptions needs to be saved in a ref, since due to onHighlightChange it is in and output of useMuiAutocomplete
    var filteredOptionsRef = (0, react_1.useRef)(null);
    var virtuosoRef = (0, react_1.useRef)(null);
    var onHighlightChange = (0, react_1.useCallback)(function (event, option, reason) {
        var _a;
        if (reason !== 'keyboard' || (event === null || event === void 0 ? void 0 : event.type) !== 'keydown' || !option) {
            return;
        }
        var index = (0, lodash_1.findIndex)(filteredOptionsRef.current, function (item) { return item === option; });
        if (index) {
            (_a = virtuosoRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
                index: index,
                behavior: 'auto',
            });
        }
    }, []);
    var _d = (0, useAutocomplete_1.default)(__assign({ 
        // The following "(true as unknown) as false" is required for the following reason:
        // Input should no be clearable, but <T, Multiple, disableClearable = true, false> does not allow
        // to set the initial value to null as is required when the component is controlled.
        // Therefore, we trick useMuiAutocomplete by using <T, Multiple, disableClearable = false, false>
        // while still passing disableClearable = true to the hook.
        clearOnBlur: true, disableClearable: true, disableCloseOnSelect: disableCloseOnSelect !== null && disableCloseOnSelect !== void 0 ? disableCloseOnSelect : multiple, disableListWrap: true, filterOptions: filterOptions, getOptionDisabled: getOptionDisabled, getOptionLabel: function (option) { return getOptionLabelWrapper(option); }, getOptionSelected: getOptionSelectedWrapper, onHighlightChange: onHighlightChange, multiple: multiple, 
        // As the useMuiAutocomplete is insufficiently typed, type casts are needed in the following
        onChange: function (_event, value) {
            onChange === null || onChange === void 0 ? void 0 : onChange(value);
        }, onInputChange: function (_, value) { return onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(value); }, options: allOptions }, (controlled
        ? { value: (value !== null && value !== void 0 ? value : (multiple ? [] : null)) }
        : { defaultValue: defaultValue }))), anchorEl = _d.anchorEl, focused = _d.focused, getInputLabelProps = _d.getInputLabelProps, getInputProps = _d.getInputProps, getListboxProps = _d.getListboxProps, getOptionProps = _d.getOptionProps, getPopupIndicatorProps = _d.getPopupIndicatorProps, getRootProps = _d.getRootProps, getTagProps = _d.getTagProps, filteredOptions = _d.groupedOptions, inputValue = _d.inputValue, popupOpen = _d.popupOpen, setAnchorEl = _d.setAnchorEl, selectedValue = _d.value;
    (0, react_1.useEffect)(function () {
        filteredOptionsRef.current = filteredOptions;
    }, [filteredOptions]);
    var groupedOptions = (0, react_1.useMemo)(function () {
        return groupBy
            ? (0, lodash_1.groupBy)((0, lodash_1.map)(filteredOptions, function (option, index) { return ({ index: index, option: option }); }), function (optionWithIndex) { return groupBy(optionWithIndex.option); })
            : {};
    }, [filteredOptions, groupBy]);
    var getInputPropsWrapper = (0, react_1.useCallback)(function () {
        var _a = getInputProps(), inputRef = _a.ref, InputProps = __rest(_a, ["ref"]);
        return __assign({ inputRef: inputRef, fontVariant: fontVariant, autoComplete: false }, InputProps);
    }, [fontVariant, getInputProps]);
    var getOptionPropsWrapper = function (option, index) {
        var optionProps = getOptionProps({
            option: option,
            index: index,
        });
        return __assign({ selected: optionProps['aria-selected'], disabled: optionProps['aria-disabled'], tooltip: getOptionTooltipText === null || getOptionTooltipText === void 0 ? void 0 : getOptionTooltipText(option) }, optionProps);
    };
    var handlePopupIconClick = function (event) {
        var onClick = getPopupIndicatorProps().onClick;
        stopEvent(event);
        onClick(event);
    };
    return {
        anchorEl: anchorEl,
        classes: classes,
        filteredOptions: filteredOptions,
        focused: focused,
        getInputLabelProps: getInputLabelProps,
        getInputProps: getInputPropsWrapper,
        getListboxProps: getListboxProps,
        getOptionLabel: getOptionLabelWrapper,
        getOptionProps: getOptionPropsWrapper,
        getRootProps: getRootProps,
        getTagProps: function (index) { return getTagProps({ index: index }); },
        groupedOptions: groupedOptions,
        handlePopupIconClick: handlePopupIconClick,
        inputValue: inputValue,
        popupOpen: popupOpen,
        setAnchorEl: setAnchorEl,
        value: selectedValue,
        virtuosoRef: virtuosoRef,
        popperRef: (0, react_1.useRef)(null),
    };
}
exports.useAutocomplete = useAutocomplete;
function stopEvent(event) {
    event.preventDefault();
    event.stopPropagation();
}
exports.stopEvent = stopEvent;
